if (!customElements.get('ncf-product-row')) {
  customElements.define(
    'ncf-product-row',
    class NcfProductRow extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('[data-scroller]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.bar = this.querySelector('[data-bar]');
        this.onPrev = () => this.move(-1);
        this.onNext = () => this.move(1);
        this.onScroll = () => this.update();
        this.onWheel = (event) => this.keepPageScroll(event);
        this.onPointerDown = (event) => this.startGesture(event);
        this.onPointerMove = (event) => this.moveGesture(event);
        this.onPointerEnd = (event) => this.endGesture(event);
        if (this.prev) this.prev.addEventListener('click', this.onPrev);
        if (this.next) this.next.addEventListener('click', this.onNext);
        if (this.scroller) {
          this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
          this.scroller.addEventListener('wheel', this.onWheel, { passive: false });
          this.scroller.addEventListener('pointerdown', this.onPointerDown);
          this.scroller.addEventListener('pointermove', this.onPointerMove, { passive: false });
          this.scroller.addEventListener('pointerup', this.onPointerEnd);
          this.scroller.addEventListener('pointercancel', this.onPointerEnd);
          this.onClickCapture = (event) => {
            if (!this.suppressClick) return;
            this.suppressClick = false;
            event.preventDefault();
            event.stopPropagation();
          };
          this.scroller.addEventListener('click', this.onClickCapture, true);
        }
        window.addEventListener('resize', this.onScroll);
        /* Text-only zoom changes card sizes without a window resize. */
        if (this.scroller && 'ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.update());
          this.resizeObserver.observe(this.scroller);
          const card = this.scroller.querySelector('[data-card]');
          if (card) this.resizeObserver.observe(card);
        }
        this.update();
        this.startAtMarked();
      }

      /*
        A row can mark one card with data-row-start (the heat ladder's current product). If it
        is out of view on load, the row opens scrolled to a snap point that puts it in view,
        roughly centered. Instant, focus untouched, and only when the row is still at its start
        and nothing in it has focus, so a restored or user-moved position is never changed.
      */
      startAtMarked() {
        if (!this.scroller || this.scroller.scrollLeft !== 0) return;
        if (this.contains(document.activeElement)) return;
        const marked = this.scroller.querySelector('[data-row-start]');
        if (!marked) return;
        const rowBox = this.scroller.getBoundingClientRect();
        const markedBox = marked.getBoundingClientRect();
        if (markedBox.left >= rowBox.left && markedBox.right <= rowBox.right) return;
        const cards = Array.from(this.scroller.querySelectorAll('[data-card]'));
        const index = cards.indexOf(marked);
        if (index < 0) return;
        const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
        const step = cards[0].getBoundingClientRect().width + gap;
        if (step <= 0) return;
        const visible = Math.max(1, Math.floor((this.scroller.clientWidth + gap) / step));
        const max = this.scroller.scrollWidth - this.scroller.clientWidth;
        const target = Math.min(max, Math.max(0, (index - Math.floor((visible - 1) / 2)) * step));
        this.scroller.scrollLeft = target;
        this.update();
      }

      disconnectedCallback() {
        if (this.prev) this.prev.removeEventListener('click', this.onPrev);
        if (this.next) this.next.removeEventListener('click', this.onNext);
        if (this.scroller) {
          this.scroller.removeEventListener('scroll', this.onScroll);
          this.scroller.removeEventListener('wheel', this.onWheel);
          this.scroller.removeEventListener('pointerdown', this.onPointerDown);
          this.scroller.removeEventListener('pointermove', this.onPointerMove);
          this.scroller.removeEventListener('pointerup', this.onPointerEnd);
          this.scroller.removeEventListener('pointercancel', this.onPointerEnd);
          this.scroller.removeEventListener('click', this.onClickCapture, true);
          this.scroller.classList.remove('is-dragging');
        }
        window.removeEventListener('resize', this.onScroll);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      startGesture(event) {
        if (!this.scroller || event.pointerType === 'mouse' || !event.isPrimary) return;
        this.gesture = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          left: this.scroller.scrollLeft,
          axis: null
        };
      }

      moveGesture(event) {
        if (!this.gesture || event.pointerId !== this.gesture.id) return;
        const dx = event.clientX - this.gesture.x;
        const dy = event.clientY - this.gesture.y;
        if (!this.gesture.axis) {
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
          this.gesture.axis = Math.abs(dx) > Math.abs(dy) * 1.25 ? 'x' : 'y';
        }
        if (this.gesture.axis !== 'x') return;
        if (Math.abs(dx) > 12) this.suppressClick = true;
        if (this.scroller.setPointerCapture) this.scroller.setPointerCapture(event.pointerId);
        this.scroller.classList.add('is-dragging');
        event.preventDefault();
        this.scroller.scrollLeft = this.gesture.left - dx;
      }

      endGesture(event) {
        if (!this.gesture || (event && event.pointerId !== this.gesture.id)) return;
        const horizontal = this.gesture.axis === 'x';
        this.gesture = null;
        if (!this.scroller) return;
        this.scroller.classList.remove('is-dragging');
        if (horizontal) this.settleRow();
      }

      settleRow() {
        const card = this.scroller.querySelector('[data-card]');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || parseFloat(getComputedStyle(this.scroller).gap) || 0;
        const step = card.getBoundingClientRect().width + gap;
        if (step <= 0) return;
        const index = Math.round(this.scroller.scrollLeft / step);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scroller.scrollTo({ left: index * step, behavior: reduce ? 'auto' : 'smooth' });
      }

      keepPageScroll(event) {
        if (event.ctrlKey) return;
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        event.preventDefault();
        const line = 16;
        const page = window.innerHeight || 0;
        const dy = event.deltaMode === 1 ? event.deltaY * line : event.deltaMode === 2 ? event.deltaY * page : event.deltaY;
        window.scrollBy(0, dy);
      }

      move(direction) {
        if (!this.scroller) return;
        const button = direction < 0 ? this.prev : this.next;
        if (button && button.getAttribute('aria-disabled') === 'true') return;
        const card = this.scroller.querySelector('[data-card]');
        const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
        /* Two cards per click, as in the mockup. */
        const distance = card ? (card.getBoundingClientRect().width + gap) * 2 : this.scroller.clientWidth;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scroller.scrollBy({ left: distance * direction, behavior: reduce ? 'auto' : 'smooth' });
      }

      setEnd(button, atEnd) {
        if (!button) return;
        if (atEnd) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      }

      update() {
        if (!this.scroller) return;
        const max = this.scroller.scrollWidth - this.scroller.clientWidth;
        /*
          aria-disabled, not disabled: a disabled button drops keyboard focus to the page, so
          pressing Next to the end of the row would lose the reader's place.
        */
        this.setEnd(this.prev, this.scroller.scrollLeft <= 2);
        this.setEnd(this.next, max <= 2 || this.scroller.scrollLeft >= max - 2);
        /* A row that fits has nothing to scroll, so it leaves the tab order. */
        this.scroller.tabIndex = max > 2 ? 0 : -1;
        if (!this.bar) return;
        /* Mockup: the red fill starts at 22% and grows to 100% at the end of the row. */
        const ratio = max <= 2 ? 1 : this.scroller.scrollLeft / max;
        this.bar.style.width = Math.round(22 + ratio * 78) + '%';
      }
    }
  );
}
